'use client'

import { calculateAllInOrderTotal, type AllInOrderPricing } from '@/lib/fee-calculation'

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useTimedCart } from '@/context/TimedCartContext'
import { trackPageView, trackViewItem, trackSelectItem } from '@/lib/timed-ui/analytics'
import EventHero from '@/components/timed-ui/events/EventHero'
import EventContentSection from '@/components/timed-ui/events/EventContentSection'
import BookingPanel from '@/components/timed-ui/booking/BookingPanel'
import MobileBookingSheet from '@/components/timed-ui/booking/MobileBookingSheet'
import SaleCountdownCard from '@/components/timed-ui/events/SaleCountdownCard'
import WaitlistForm from '@/components/timed-ui/events/WaitlistForm'
import { LayoutGrid } from 'lucide-react'
import DatePillStrip from '@/components/timed-ui/booking/DatePillStrip'
import TimeSlotGrid from '@/components/timed-ui/booking/TimeSlotGrid'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { getTemplateConfig } from '@/lib/templates/template-config'
import { setAffiliateCode } from '@/lib/affiliate-tracking'
import RichDescription from '@/components/timed-ui/shared/RichDescription'
import {
  adaptEventForUI,
  adaptTicketTypeForUI,
  pickerMax,
  adaptSlotsToWaveTimeSlots,
  type TimedUIEvent,
  type TimedUITicketType,
  type DateEvent,
  type WaveTimeSlot,
  type DayTabConfig,
} from '@/lib/timed-ui/adapters'


interface EventData {
  id: string
  slug: string
  name: string
  eventType?: string
  timezone?: string
  showDescriptionFirst?: boolean
  description: string
  startDate: Date
  endDate?: Date
  imageUrl?: string
  bannerUrl?: string
  bannerDisplayMode?: string
  squareImageUrl?: string
  videoUrl?: string
  videoEmbedOptions?: {
    autoplay?: boolean
    mute?: boolean
    loop?: boolean
    controls?: boolean
    startTime?: number
  } | null
  presaleEnabled?: boolean
  presaleStartDate?: string | null
  presaleEndDate?: string | null
  publicSaleStartDate?: string | null
  saleEndDate?: string | null
  venue: {
    id: string
    name: string
    address: string | null
    city: string | null
    state: string | null
    capacity?: number | null
  }
  ticketTypes: Array<{
    id: string
    name: string
    description: string
    price: number
    allInPrice: number
    quantity: number
    sold: number
    reserved: number
    isVisible: boolean
    sortOrder: number
    minQuantity: number
    maxQuantity: number
    maxPerCustomer?: number | null
    quantityInterval: number
    isAvailable: boolean
    availableQuantity: number
    badgeText: string | null
    backgroundColor: string | null
    textColor: string | null
    isFeature: boolean
    maxScans?: number | null
    issuePerDay?: boolean
    includesPerks?: string[]
    [key: string]: unknown
  }>
  galleryImages?: string[]
  dayTabConfig?: DayTabConfig | null
  schedules?: Array<{
    id: string
    startDate: string
    endDate: string
    recurrenceRule: string | null
    timeSlots: Array<{
      id: string
      startTime: string
      endTime: string
      capacity: number
      price: number | null
      label: string | null
      dayOfWeek: number | null
    }>
    blackoutDates: Array<{
      date: string
      reason: string | null
    }>
  }>
}

// --- Sale phase countdown ---
type SalePhase =
  | { type: 'presale_upcoming'; date: Date }
  | { type: 'presale_active'; endDate: Date | null }
  | { type: 'public_upcoming'; date: Date }
  | { type: 'on_sale' }
  | { type: 'sale_ended' }
  | null

function getEventSalePhase(event: EventData): SalePhase {
  const now = new Date()
  // Sale ends at the configured saleEndDate, or — if unset — when the
  // event itself ends (endDate) or starts (startDate as last resort).
  // Without this fallback an event with saleEndDate null stays "on
  // sale" indefinitely after its date passes, and the direct URL
  // accepts purchases for events that already happened.
  const effectiveSaleEnd =
    event.saleEndDate
      ? new Date(event.saleEndDate)
      : event.endDate
        ? new Date(typeof event.endDate === 'string' ? event.endDate : event.endDate)
        : new Date(event.startDate)
  if (effectiveSaleEnd && now > effectiveSaleEnd) {
    return { type: 'sale_ended' }
  }
  if (event.presaleEnabled && event.presaleStartDate) {
    const presaleStart = new Date(event.presaleStartDate)
    if (now < presaleStart) {
      return { type: 'presale_upcoming', date: presaleStart }
    }
    // Presale has started
    if (event.presaleEndDate) {
      const presaleEnd = new Date(event.presaleEndDate)
      if (now < presaleEnd) {
        return { type: 'presale_active', endDate: presaleEnd }
      }
    }
  }
  if (event.publicSaleStartDate) {
    const publicStart = new Date(event.publicSaleStartDate)
    if (now < publicStart) {
      return { type: 'public_upcoming', date: publicStart }
    }
  }
  return null
}

interface EventDetailsClientProps {
  event: EventData
  eventStatus?: string
  accessLocked?: boolean
  /** The fee inputs the CHARGE uses, so a multi-ticket total is computed not multiplied. */
  orderPricing?: AllInOrderPricing
  /** Set when this visit came through a private (sponsor) link: whose. */
  privateAccess?: { name: string; message?: string | null; maxPerOrder?: number | null } | null
}

function EventGallery({ images }: { images: string[] }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null)
      else if (e.key === 'ArrowLeft') setLightboxIndex((lightboxIndex - 1 + images.length) % images.length)
      else if (e.key === 'ArrowRight') setLightboxIndex((lightboxIndex + 1) % images.length)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [lightboxIndex, images.length])

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [lightboxIndex])

  if (!images || images.length === 0) return null

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current || lightboxIndex === null) return
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    touchStartRef.current = null
    // Only swipe if horizontal movement > 50px and greater than vertical
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
    if (dx > 0) setLightboxIndex((lightboxIndex - 1 + images.length) % images.length)
    else setLightboxIndex((lightboxIndex + 1) % images.length)
  }

  return (
    <>
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Gallery</h2>
        <div className={`grid gap-3 ${
          images.length === 1 ? 'grid-cols-1 max-w-2xl' :
          images.length === 2 ? 'grid-cols-2 max-w-3xl' :
          'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        }`}>
          {images.map((url, i) => (
            <button
              key={i}
              onClick={() => setLightboxIndex(i)}
              className={`relative rounded-xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary ${
                images.length === 1 ? 'aspect-video' : 'aspect-[4/3]'
              }`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={url}
                alt={`Gallery image ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + images.length) % images.length) }}
                className="absolute left-4 text-white/80 hover:text-white p-2 z-10 hidden md:block"
                aria-label="Previous"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % images.length) }}
                className="absolute right-4 text-white/80 hover:text-white p-2 z-10 hidden md:block"
                aria-label="Next"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={images[lightboxIndex]}
            alt={`Gallery image ${lightboxIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain rounded-lg select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />

          {images.length > 1 && (
            <div className="absolute bottom-4 text-white/60 text-sm">
              {lightboxIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  )
}

function LockedBookingCard({ slug }: { slug: string }) {
  return (
    <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <p className="text-text-primary font-semibold">Members Only</p>
      <p className="text-text-secondary text-sm mt-1">Sign in with your member account to purchase tickets.</p>
      <a
        href={`/accounts/login?redirect=/events/${slug}`}
        className="inline-block mt-4 px-6 py-2.5 bg-primary text-text-inverse rounded-btn text-sm font-medium hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  )
}

export default function EventDetailsClient({ event: rawEvent, eventStatus = 'ON_SALE', accessLocked = false, orderPricing, privateAccess = null }: EventDetailsClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { addItem, closeCart } = useTimedCart()
  const { branding } = useTheme()
  const formatPrice = useFormatPrice()
  const template = getTemplateConfig(branding?.layout)
  const layout = template.eventDetails.layout
  const position = template.eventDetails.bookingPanelPosition

  const isTimedEvent = rawEvent.eventType === 'TIMED_ENTRY'
  const isSeatedEvent = rawEvent.eventType === 'RESERVED_SEATING'

  const [selectedTickets, setSelectedTickets] = useState<Record<string, number>>({})
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [presaleUnlocked, setPresaleUnlocked] = useState(false)
  const [presaleCode, setPresaleCode] = useState('')
  const [presaleError, setPresaleError] = useState('')
  const [presaleVerifying, setPresaleVerifying] = useState(false)

  // Timed event state
  const [availableDates, setAvailableDates] = useState<DateEvent[]>([])
  const [selectedDate, setSelectedDate] = useState<DateEvent | null>(null)
  const [availableTimeSlots, setAvailableTimeSlots] = useState<WaveTimeSlot[]>([])
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<WaveTimeSlot | null>(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const timeSlotSectionRef = useRef<HTMLDivElement>(null)
  const ticketSectionRef = useRef<HTMLDivElement>(null)
  const mobileSheetExpandRef = useRef<(() => void) | null>(null)

  // Track page view and item view
  useEffect(() => {
    // Filter first: an event of only add-ons sent Infinity to analytics.
    const admissionPrices = rawEvent.ticketTypes.filter(t => !t.isAddon).map(t => t.price)
    const minPrice = admissionPrices.length > 0 ? Math.min(...admissionPrices) : 0
    trackPageView(rawEvent.name, window.location.href)
    trackViewItem({ id: rawEvent.id, name: rawEvent.name, category: rawEvent.eventType || 'Event', price: minPrice })
  }, [rawEvent.id, rawEvent.name, rawEvent.eventType, rawEvent.ticketTypes])

  // Affiliate tracking: check URL for ref or utm_source+utm_medium=affiliate
  useEffect(() => {
    const ref = searchParams.get('ref')
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const affiliateCode = ref || (utmMedium === 'affiliate' && utmSource ? utmSource : null)
    if (!affiliateCode) return

    setAffiliateCode(rawEvent.id, affiliateCode)

    // Fire-and-forget click tracking
    fetch('/api/affiliates/track-click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ affiliateCode, eventId: rawEvent.id }),
    }).catch(() => {})
  }, [rawEvent.id, searchParams])

  // Fetch dates that actually have available timeslots (real-time inventory check).
  // This hides dates where all slots are sold out or already past.
  // Uses sessionStorage to cache results and avoid re-fetching on every mount.
  useEffect(() => {
    if (!isTimedEvent) return

    const cacheKey = `avail-dates-${rawEvent.id}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      try {
        const { dates, ts } = JSON.parse(cached)
        // Use cache if less than 60 seconds old
        if (Date.now() - ts < 60_000 && Array.isArray(dates)) {
          const dateEvents: DateEvent[] = dates.map((dateStr: string) => {
            const [y, m, d] = dateStr.split('-').map(Number)
            const dt = new Date(y, m - 1, d, 12, 0, 0)
            return {
              id: `date-${dateStr}`,
              start: Math.floor(dt.getTime() / 1000),
              startDate: dateStr,
              sold: '0',
              availableQuantity: 1,
              quantity: 1,
            }
          })
          setAvailableDates(dateEvents)
          return
        }
      } catch { /* cache miss, fetch fresh */ }
    }

    const fetchAvailableDates = async () => {
      try {
        const dateParams = new URLSearchParams()
        const eventStart = typeof rawEvent.startDate === 'string' ? rawEvent.startDate : rawEvent.startDate.toISOString()
        const eventStartDate = eventStart.split('T')[0]
        const todayDate = new Date().toISOString().split('T')[0]
        dateParams.set('from', eventStartDate > todayDate ? eventStartDate : todayDate)
        if (rawEvent.endDate) {
          const eventEnd = typeof rawEvent.endDate === 'string' ? rawEvent.endDate : rawEvent.endDate.toISOString()
          dateParams.set('to', eventEnd.split('T')[0])
        }
        const res = await fetch(`/api/events/${rawEvent.id}/available-dates?${dateParams}`)
        const data = await res.json()
        if (data.success && data.dates) {
          // Cache the raw date strings
          sessionStorage.setItem(cacheKey, JSON.stringify({ dates: data.dates, ts: Date.now() }))
          const dateEvents: DateEvent[] = data.dates.map((dateStr: string) => {
            const [y, m, d] = dateStr.split('-').map(Number)
            const dt = new Date(y, m - 1, d, 12, 0, 0)
            return {
              id: `date-${dateStr}`,
              start: Math.floor(dt.getTime() / 1000),
              startDate: dateStr,
              sold: '0',
              availableQuantity: 1,
              quantity: 1,
            }
          })
          setAvailableDates(dateEvents)
        }
      } catch (error) {
        console.error('Error fetching available dates:', error)
      }
    }

    fetchAvailableDates()
  }, [isTimedEvent, rawEvent.id])

  // Adapt the server data to the TimedUI format
  const event: TimedUIEvent = useMemo(() => adaptEventForUI({
    id: rawEvent.id,
    name: rawEvent.name,
    slug: rawEvent.slug,
    description: rawEvent.description,
    startDate: typeof rawEvent.startDate === 'string' ? rawEvent.startDate : rawEvent.startDate.toISOString(),
    endDate: rawEvent.endDate ? (typeof rawEvent.endDate === 'string' ? rawEvent.endDate : rawEvent.endDate.toISOString()) : null,
    timezone: rawEvent.timezone || 'America/New_York',
    status: eventStatus || 'ON_SALE',
    imageUrl: rawEvent.imageUrl || null,
    bannerUrl: rawEvent.bannerUrl || null,
    bannerDisplayMode: rawEvent.bannerDisplayMode || null,
    squareImageUrl: rawEvent.squareImageUrl || null,
    videoUrl: rawEvent.videoUrl || null,
    videoEmbedOptions: rawEvent.videoEmbedOptions || null,
    venue: rawEvent.venue ? {
      id: rawEvent.venue.id,
      name: rawEvent.venue.name,
      address: rawEvent.venue.address,
      city: rawEvent.venue.city,
      state: rawEvent.venue.state,
      zipCode: null,
      capacity: rawEvent.venue.capacity || null,
      latitude: null,
      longitude: null,
    } : null,
    ticketTypes: rawEvent.ticketTypes,
  }), [rawEvent])

  // Max per customer: what a signed-in buyer already holds of each type, so
  // the picker stops at what is left (lib/checkout/max-per-customer). Payment
  // enforces it for everyone; this only makes the picker honest early.
  const [perCustomerAlready, setPerCustomerAlready] = useState<Record<string, number>>({})
  useEffect(() => {
    if (!rawEvent.ticketTypes.some(tt => typeof tt.maxPerCustomer === 'number')) return
    let cancelled = false
    fetch(`/api/events/${rawEvent.id}/customer-limits`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.already || typeof data.already !== 'object') return
        setPerCustomerAlready(data.already as Record<string, number>)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [rawEvent.id, rawEvent.ticketTypes])

  const ticketTypes: TimedUITicketType[] = useMemo(() =>
    rawEvent.ticketTypes.map(tt => adaptTicketTypeForUI({ ...tt, perCustomerAlready: perCustomerAlready[tt.id] ?? 0 })),
    [rawEvent.ticketTypes, perCustomerAlready]
  )

  // Split ticket types: season passes (maxScans === 0) skip date/time selection
  const seasonPassTickets = useMemo(() => ticketTypes.filter(t => !t.isAddon && t.maxScans === 0 && t.isVisible !== false), [ticketTypes])
  const regularTickets = useMemo(() => ticketTypes.filter(t => !t.isAddon && t.maxScans !== 0), [ticketTypes])
  // Addons are shown at checkout only, not on the event details page

  // Separate state for season pass quantities (independent of date/time selection)
  const [seasonPassQuantities, setSeasonPassQuantities] = useState<Record<string, number>>({})

  const handleSeasonPassQuantityChange = useCallback((ticketTypeId: string, delta: number) => {
    setSeasonPassQuantities(prev => {
      const tt = seasonPassTickets.find(t => t.id === ticketTypeId)
      if (!tt) return prev
      const current = prev[ticketTypeId] || 0
      const next = Math.max(0, Math.min(current + delta, pickerMax(tt)))
      if (next === 0) {
        const { [ticketTypeId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [ticketTypeId]: next }
    })
  }, [seasonPassTickets])

  const handleSeasonPassCheckout = useCallback(() => {
    const hasItems = Object.values(seasonPassQuantities).some(q => q > 0)
    if (!hasItems) return
    setCheckoutLoading(true)
    try {
      Object.entries(seasonPassQuantities).forEach(([ticketTypeId, quantity]) => {
        if (quantity > 0) {
          const ticketType = seasonPassTickets.find(t => t.id === ticketTypeId)
          if (ticketType) {
            addItem({
              eventId: rawEvent.id,
              eventTitle: rawEvent.name,
              eventStart: Math.floor(new Date(rawEvent.startDate).getTime() / 1000),
              ticketTypeId: ticketType.id,
              ticketTypeName: ticketType.name,
              quantity,
              price: ticketType.price,
              allInPrice: ticketType.allInPrice,
              // No waveTimeId or selectedDate — season passes are not date-specific
            }, { openDrawer: false })
          }
        }
      })
      closeCart()
      router.push('/checkout')
    } catch {
      setCheckoutLoading(false)
    }
  }, [seasonPassQuantities, seasonPassTickets, rawEvent, addItem, closeCart, router])

  const handleTicketQuantityChange = useCallback((ticketTypeId: string, quantity: number) => {
    setSelectedTickets(prev => ({
      ...prev,
      [ticketTypeId]: quantity,
    }))
  }, [])

  // Embed handoff (v2): when a buyer selected quantities in an embedded widget
  // on another site, "Checkout" opens this page with ?buy=id:qty,id:qty. Pre-fill
  // the selection so they land ready to check out. General-admission only
  // (seated/timed use their own seat/slot pickers). One-shot + additive: clamps
  // to each type's max, skips add-ons, and does nothing without the param.
  const embedSeedRef = useRef(false)
  // When the embed sent ?checkout=1, advance straight to checkout after seeding
  // (handled by a separate effect once state + on-sale guards are ready).
  const pendingEmbedCheckoutRef = useRef(false)
  const didEmbedCheckoutRef = useRef(false)
  useEffect(() => {
    if (embedSeedRef.current) return
    if (isTimedEvent || isSeatedEvent) return
    const buy = searchParams.get('buy')
    if (!buy) return
    embedSeedRef.current = true
    const seed: Record<string, number> = {}
    for (const pair of buy.split(',')) {
      const [id, q] = pair.split(':')
      const tt = ticketTypes.find(t => t.id === id)
      if (!tt || tt.isAddon) continue
      const requested = parseInt(q, 10)
      if (!Number.isFinite(requested) || requested <= 0) continue
      const cap = pickerMax(tt)
      const qty = Math.min(requested, cap)
      if (qty > 0) seed[id] = qty
    }
    if (Object.keys(seed).length > 0) {
      setSelectedTickets(seed)
      if (searchParams.get('checkout') === '1') pendingEmbedCheckoutRef.current = true
    }
  }, [searchParams, ticketTypes, isTimedEvent, isSeatedEvent])

  const totalPrice = useMemo(() => {
    /**
     * COMPUTED, not multiplied.
     *
     * `allInPrice` is the ADVERTISED single-ticket total, which CLAUDE.md defines
     * as the unit price including fixed fees - correct at a quantity of one. But a
     * fixed fee is per ORDER unless EventFee.perTicket is set, so multiplying it
     * by the quantity charged the fee once per ticket in the DISPLAY while
     * checkout charged it once. Three $59 tickets were advertised at $177.00 and
     * charged $172.00, and at a quantity of two the same checkout screen showed
     * "Your Tickets 2 $118.00" above "Complete Order - $115.50".
     *
     * The subtotal is built from base prices and the fees are applied once, over
     * the real ticket count - the same call the charge makes.
     */
    let subtotal = 0
    let count = 0
    for (const [ticketTypeId, quantity] of Object.entries(selectedTickets)) {
      const ticket = ticketTypes.find(t => t.id === ticketTypeId)
      if (!ticket) continue
      subtotal += ticket.price * quantity
      count += quantity
    }
    if (subtotal <= 0) return 0
    // Without the fee inputs, fall back to the old behaviour rather than quoting
    // a bare subtotal with no fees at all, which would understate every order.
    if (!orderPricing) {
      return Object.entries(selectedTickets).reduce((total, [ticketTypeId, quantity]) => {
        const ticket = ticketTypes.find(t => t.id === ticketTypeId)
        return ticket ? total + ticket.allInPrice * quantity : total
      }, 0)
    }
    return calculateAllInOrderTotal(subtotal, count, orderPricing)
  }, [selectedTickets, ticketTypes, orderPricing])

  const ticketCount = useMemo(() => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0)
  }, [selectedTickets])

  // Timed event handlers
  const handleDateSelect = useCallback(async (dateEvent: DateEvent) => {
    setSelectedDate(dateEvent)
    setSelectedTimeSlot(null)
    setSelectedTickets({})
    setLoadingSlots(true)
    trackSelectItem('date', { name: dateEvent.startDate, id: dateEvent.id })

    let slotCount = 0
    try {
      const dateStr = dateEvent.startDate
      const res = await fetch(`/api/events/${rawEvent.id}/available-slots?date=${dateStr}`)
      const data = await res.json()

      if (data.success && data.slots) {
        const adapted = adaptSlotsToWaveTimeSlots(data.slots)
        slotCount = adapted.length
        setAvailableTimeSlots(adapted)
        // Auto-select when only one slot (e.g. all-day waterpark events)
        if (adapted.length === 1) {
          setSelectedTimeSlot(adapted[0])
        }
      } else {
        setAvailableTimeSlots([])
      }
    } catch (error) {
      console.error('Error fetching time slots:', error)
      setAvailableTimeSlots([])
    } finally {
      setLoadingSlots(false)
      setTimeout(() => {
        if (window.matchMedia('(max-width: 1023px)').matches) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          // Skip to tickets if single slot was auto-selected, otherwise show time picker
          const target = slotCount <= 1 ? ticketSectionRef : timeSlotSectionRef
          target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 200)
    }
  }, [rawEvent.id])

  const handleTimeSlotSelect = useCallback((timeSlot: WaveTimeSlot) => {
    setSelectedTimeSlot(timeSlot)
    setSelectedTickets({})
    trackSelectItem('timeslot', { name: timeSlot.waveTime || timeSlot.startTime || '', id: timeSlot.id })
    // Auto-scroll to the ticket selection section
    setTimeout(() => {
      if (window.matchMedia('(max-width: 1023px)').matches) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        ticketSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 150)
  }, [])

  const handleCheckout = useCallback(() => {
    setCheckoutLoading(true)

    try {
      Object.entries(selectedTickets).forEach(([ticketTypeId, quantity]) => {
        if (quantity > 0) {
          const ticketType = ticketTypes.find(t => t.id === ticketTypeId)
          if (ticketType) {
            // For timed events, use the selected visit date instead of the schedule start
            const cartEventStart = isTimedEvent && selectedDate?.startDate
              ? Math.floor(new Date(`${selectedDate.startDate}T12:00:00`).getTime() / 1000)
              : Math.floor(new Date(rawEvent.startDate).getTime() / 1000)

            addItem({
              eventId: rawEvent.id,
              eventTitle: rawEvent.name,
              eventStart: cartEventStart,
              ticketTypeId: ticketType.id,
              ticketTypeName: ticketType.name,
              quantity,
              price: ticketType.price,
              allInPrice: ticketType.allInPrice,
              ...(isTimedEvent && selectedTimeSlot ? {
                waveTimeId: selectedTimeSlot.id,
                selectedDate: selectedDate?.startDate || null,
              } : {}),
            }, { openDrawer: false })
          }
        }
      })

      closeCart()
      router.push('/checkout')
    } catch {
      setCheckoutLoading(false)
    }
  }, [selectedTickets, ticketTypes, rawEvent, addItem, closeCart, router, isTimedEvent, selectedTimeSlot])

  const bookingProps = {
    ticketTypes,
    regularTickets,
    selectedTickets,
    onTicketQuantityChange: handleTicketQuantityChange,
    totalPrice,
    ticketCount,
    onCheckout: handleCheckout,
    loadingTickets: false,
    checkoutLoading,
    slotCapacity: selectedTimeSlot?.availableQuantity,
    // A sponsor link's per-order limit caps the whole order, not each type.
    orderLimit: privateAccess?.maxPerOrder ?? null,
    dayTabConfig: rawEvent.dayTabConfig || null,
    timezone: rawEvent.timezone,
  }

  const handlePresaleVerify = useCallback(async () => {
    const code = presaleCode.trim()
    if (!code) return
    setPresaleError('')
    setPresaleVerifying(true)
    try {
      const res = await fetch(`/api/events/${rawEvent.id}/presale-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      const data = await res.json()
      if (data.success) {
        setPresaleUnlocked(true)
        setPresaleError('')
      } else {
        setPresaleError(data.error || 'Invalid access code')
      }
    } catch {
      setPresaleError('Verification failed. Please try again.')
    } finally {
      setPresaleVerifying(false)
    }
  }, [presaleCode, rawEvent.id])

  // Sale window: any detected phase means tickets aren't on general sale yet (or sale ended).
  // Only when all phases have passed (null) is the event on general sale.
  // Exception: if presale is active AND user has unlocked with a valid code, allow purchasing.
  const salePhase = getEventSalePhase(rawEvent)
  const saleWindowClosed = salePhase !== null && !(salePhase.type === 'presale_active' && presaleUnlocked)

  // Embed auto-checkout: a buyer who picked quantities in an embedded widget and
  // arrived with ?buy=…&checkout=1 goes straight to checkout — but ONLY when the
  // event is genuinely on sale. Entirely gated behind the embed param, so normal
  // storefront visits (which never carry checkout=1) are unaffected. One-shot.
  useEffect(() => {
    if (!pendingEmbedCheckoutRef.current || didEmbedCheckoutRef.current) return
    if (isTimedEvent || isSeatedEvent) return
    if (saleWindowClosed || accessLocked) return
    if (eventStatus === 'SOLD_OUT' || eventStatus === 'CANCELLED' || eventStatus === 'POSTPONED') return
    if (!Object.values(selectedTickets).some(q => q > 0)) return
    didEmbedCheckoutRef.current = true
    handleCheckout()
  }, [selectedTickets, saleWindowClosed, accessLocked, eventStatus, isTimedEvent, isSeatedEvent, handleCheckout])

  // Show tickets only after time slot is selected for timed events
  const showTicketSelection = !isTimedEvent || !!selectedTimeSlot

  return (
    <div className="bg-surface-secondary w-full max-w-full">
      {/* Back navigation */}
      <div className="max-w-page mx-auto px-4 lg:px-8 pt-4 pb-2">
        <button
          onClick={() => router.push('/')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-tertiary transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Events
        </button>
      </div>

      {/* Private (sponsor) link: this visit sees that link's tickets only */}
      {privateAccess && (
        <div className="max-w-page mx-auto px-4 lg:px-8 pb-2">
          <div className="bg-surface border border-surface-border rounded-xl px-5 py-4 shadow-card flex items-center gap-3" data-private-access>
            <div className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-text-primary font-semibold leading-tight">Private access: {privateAccess.name}</p>
              {privateAccess.message
                ? <p className="text-text-secondary text-sm whitespace-pre-line">{privateAccess.message}</p>
                : <p className="text-text-secondary text-sm">These tickets are available through your private link.</p>}
              {privateAccess.maxPerOrder ? (
                <p className="text-text-secondary text-xs mt-1">Limit {privateAccess.maxPerOrder} ticket{privateAccess.maxPerOrder === 1 ? '' : 's'} per order.</p>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Access locked banner — event requires membership tags to purchase */}
      {accessLocked && (
        <div className="max-w-page mx-auto px-4 lg:px-8 pb-2">
          <div className="bg-surface border border-surface-border rounded-xl p-5 text-center shadow-card">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-3">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-text-primary font-semibold">Members Only Event</p>
            <p className="text-text-secondary text-sm mt-1">Sign in with your member account to purchase tickets.</p>
            <a
              href={`/accounts/login?redirect=/events/${rawEvent.slug}`}
              className="inline-block mt-4 px-6 py-2.5 bg-primary text-text-inverse rounded-btn text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Sign In
            </a>
          </div>
        </div>
      )}

      {/* Status banners — only for terminal states (sold out / cancelled) */}
      {eventStatus === 'SOLD_OUT' && (
        <div className="max-w-page mx-auto px-4 lg:px-8 pb-2 space-y-3">
          <div className="bg-error/10 border border-error/20 rounded-xl p-4 text-center">
            <p className="text-error font-semibold">This event is sold out</p>
            <p className="text-text-secondary text-sm mt-1">This event is sold out.</p>
          </div>
          {/* Waitlist capture — only when the tenant has the feature enabled. */}
          {branding?.publicSite?.features?.enableWaitlist && (
            <div className="max-w-md mx-auto">
              <WaitlistForm eventId={rawEvent.id} />
            </div>
          )}
        </div>
      )}
      {eventStatus === 'CANCELLED' && (
        <div className="max-w-page mx-auto px-4 lg:px-8 pb-2">
          <div className="bg-surface-tertiary border border-surface-border rounded-xl p-4 text-center">
            <p className="text-text-primary font-semibold">This event has been cancelled</p>
            <p className="text-text-secondary text-sm mt-1">This event is no longer taking place. If you purchased tickets, you will be contacted about refunds.</p>
          </div>
        </div>
      )}
      {eventStatus === 'POSTPONED' && (
        <div className="max-w-page mx-auto px-4 lg:px-8 pb-2">
          <div className="bg-surface-tertiary border border-surface-border rounded-xl p-4 text-center">
            <p className="text-text-primary font-semibold">This event has been postponed</p>
            <p className="text-text-secondary text-sm mt-1">New dates will be announced soon. Please check back for updates.</p>
          </div>
        </div>
      )}

      <EventHero event={event} />

      {isSeatedEvent && eventStatus !== 'CANCELLED' && eventStatus !== 'POSTPONED' && eventStatus !== 'SOLD_OUT' && !saleWindowClosed && !accessLocked ? (
        /* Reserved seating event: prominent CTA then event info */
        <>
          <div className="max-w-page mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-8">
            {/* Select Seats CTA - visible on all devices above content */}
            <Link
              href={`/events/${rawEvent.slug}/select-seats${searchParams?.get('preview') === '1' ? '?preview=1' : ''}`}
              className="flex items-center justify-between gap-4 w-full bg-surface rounded-2xl border border-surface-border shadow-card p-4 lg:p-5 hover:border-[rgb(var(--color-primary)/0.4)] transition-colors group"
              prefetch={true}
            >
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-[rgb(var(--color-primary)/0.1)] flex items-center justify-center flex-shrink-0">
                  <LayoutGrid className="w-5 h-5 lg:w-6 lg:h-6 text-[rgb(var(--color-primary))]" />
                </div>
                <div>
                  <h3 className="text-base lg:text-lg font-semibold text-text-primary">Reserved Seating</h3>
                  <p className="text-xs lg:text-sm text-text-secondary">Choose your exact seats from our interactive venue map</p>
                </div>
              </div>
              <span className="btn-primary px-4 py-2 lg:px-6 lg:py-2.5 flex-shrink-0 text-sm lg:text-base group-hover:scale-105 transition-transform">
                Select Seats
              </span>
            </Link>

            <EventContentSection event={event} />
            <EventGallery images={rawEvent.galleryImages || []} />
          </div>
        </>
      ) : (
        <div className="max-w-page mx-auto px-4 lg:px-8 py-8 lg:py-10">
          {/* Layout wrapper - template-aware */}
          <div className={
            layout === 'sidebar' ? 'lg:grid lg:grid-cols-3 lg:gap-8' :
            layout === 'floating' ? 'relative' : 'space-y-8'
          }>
            {/* Event content */}
            <div className={`pb-20 lg:pb-0 ${layout === 'sidebar' ? 'lg:col-span-2' : ''}`}>
              {/* Mobile CTA card - matches seated event's "Select Seats" position */}
              {eventStatus !== 'CANCELLED' && eventStatus !== 'POSTPONED' && eventStatus !== 'SOLD_OUT' && !saleWindowClosed && !accessLocked && (
                <button
                  onClick={() => {
                    if (isTimedEvent) {
                      // Scroll to bottom where the date picker bar is
                      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    } else {
                      // Expand the mobile booking sheet
                      mobileSheetExpandRef.current?.()
                    }
                  }}
                  className="flex items-center justify-between gap-4 w-full bg-surface rounded-2xl border border-surface-border shadow-card p-4 hover:border-[rgb(var(--color-primary)/0.4)] transition-colors group mb-6 lg:hidden"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[rgb(var(--color-primary)/0.1)] flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-[rgb(var(--color-primary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-base font-semibold text-text-primary">
                        {isTimedEvent ? 'Select Date & Time' : 'General Admission'}
                      </h3>
                      <p className="text-xs text-text-secondary">
                        {isTimedEvent
                          ? 'Pick your preferred date and time slot'
                          : 'Choose your tickets and quantities'}
                      </p>
                    </div>
                  </div>
                  <span className="btn-primary px-4 py-2 flex-shrink-0 text-sm group-hover:scale-105 transition-transform">
                    {isTimedEvent ? 'Select Date' : 'Get Tickets'}
                  </span>
                </button>
              )}

              {/* Mobile Season Pass section — inline in content, visible without date selection */}
              {isTimedEvent && seasonPassTickets.length > 0 && !saleWindowClosed && eventStatus !== 'CANCELLED' && eventStatus !== 'POSTPONED' && eventStatus !== 'SOLD_OUT' && !accessLocked && (
                <div id="season-pass-section" className="lg:hidden mb-6 bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden scroll-mt-20">
                  <div className="p-4 border-b border-surface-border">
                    <h3 className="text-base font-semibold text-text-primary">Season Passes</h3>
                    {rawEvent.endDate && (
                      <p className="text-xs text-text-tertiary mt-0.5">
                        Valid {new Date(rawEvent.startDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric' })} - {new Date(rawEvent.endDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    {seasonPassTickets.map(tt => (
                      <div key={tt.id} className="border border-surface-border rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-text-primary text-sm">{tt.name}</h4>
                          {tt.badgeText && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: tt.backgroundColor || 'rgb(var(--color-primary))', color: tt.textColor || '#fff' }}>
                              {tt.badgeText}
                            </span>
                          )}
                        </div>
                        {tt.description && <div className="mb-2"><RichDescription html={tt.description} className="text-xs text-text-secondary" expandable={false} /></div>}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-text-primary">{tt.allInPrice > 0 ? formatPrice(tt.allInPrice) : 'Free'}</span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleSeasonPassQuantityChange(tt.id, -1)} disabled={!seasonPassQuantities[tt.id]} className="w-10 h-10 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" /></svg>
                            </button>
                            <span className="w-8 text-center text-sm font-semibold text-text-primary tabular-nums">{seasonPassQuantities[tt.id] || 0}</span>
                            <button onClick={() => handleSeasonPassQuantityChange(tt.id, 1)} disabled={(seasonPassQuantities[tt.id] || 0) >= Math.min(pickerMax(tt), tt.availableQuantity)} className="w-10 h-10 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {Object.values(seasonPassQuantities).some(q => q > 0) && (
                      <button onClick={handleSeasonPassCheckout} disabled={checkoutLoading} className="w-full btn-primary py-3 text-sm font-semibold">
                        {checkoutLoading ? 'Processing...' : 'Get Season Pass'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <EventContentSection event={event} />
              <EventGallery images={rawEvent.galleryImages || []} />
            </div>

            {/* Desktop booking panel - position based on template */}
            <div className="hidden lg:block">
              {eventStatus === 'CANCELLED' || eventStatus === 'POSTPONED' || eventStatus === 'SOLD_OUT' || saleWindowClosed ? (
                <div className={position === 'right' ? 'lg:col-span-1 sticky top-[72px]' : 'sticky top-[72px]'}>
                  <SaleCountdownCard event={rawEvent} eventStatus={eventStatus} />
                  {salePhase?.type === 'presale_active' && !presaleUnlocked && (
                    <div className="mt-4 bg-surface rounded-2xl border border-surface-border shadow-card p-5">
                      <h3 className="text-sm font-semibold text-text-primary mb-1">Have a pre-sale code?</h3>
                      <p className="text-xs text-text-secondary mb-3">Enter your access code to unlock early ticket purchasing.</p>
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={presaleCode}
                          onChange={(e) => { setPresaleCode(e.target.value.toUpperCase()); setPresaleError('') }}
                          onKeyDown={(e) => { if (e.key === 'Enter') handlePresaleVerify() }}
                          placeholder="Enter code..."
                          className="flex-1 min-w-0 px-3 py-2 bg-surface-secondary border border-surface-border rounded-btn text-text-primary placeholder:text-text-tertiary focus:ring-2 focus:ring-primary focus:border-primary uppercase"
                          style={{ fontSize: '16px' }}
                          disabled={presaleVerifying}
                        />
                        <button
                          onClick={handlePresaleVerify}
                          disabled={presaleVerifying || !presaleCode.trim()}
                          className="shrink-0 px-4 py-2 bg-primary text-text-inverse rounded-btn text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
                        >
                          {presaleVerifying ? 'Verifying...' : 'Unlock'}
                        </button>
                      </div>
                      {presaleError && (
                        <p className="text-xs text-error mt-2">{presaleError}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : isTimedEvent ? (
                <>
                  {/* Timed event date/time/ticket selection */}
                  {position === 'right' && (
                    <div className="lg:col-span-1 sticky top-[72px] space-y-4">
                      {/* Season Pass section — always visible, no date/time required */}
                      {seasonPassTickets.length > 0 && (
                        <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                          <div className="p-5 border-b border-surface-border">
                            <h3 className="text-lg font-semibold text-text-primary">Season Passes</h3>
                            {rawEvent.endDate && (
                              <p className="text-xs text-text-tertiary mt-1">
                                Valid {new Date(rawEvent.startDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric' })} through {new Date(rawEvent.endDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            {seasonPassTickets.map(tt => (
                              <div key={tt.id} className="border border-surface-border rounded-xl p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-text-primary text-sm">{tt.name}</h4>
                                      {tt.badgeText && (
                                        <span
                                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor: tt.backgroundColor || 'rgb(var(--color-primary))',
                                            color: tt.textColor || '#fff',
                                          }}
                                        >
                                          {tt.badgeText}
                                        </span>
                                      )}
                                    </div>
                                    {tt.description && (
                                      <RichDescription html={tt.description} className="text-xs text-text-secondary" expandable={false} />
                                    )}
                                    <div className="text-sm font-bold text-text-primary mt-2">
                                      {tt.allInPrice > 0 ? formatPrice(tt.allInPrice) : 'Free'}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      onClick={() => handleSeasonPassQuantityChange(tt.id, -1)}
                                      disabled={!seasonPassQuantities[tt.id]}
                                      className="w-9 h-9 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" /></svg>
                                    </button>
                                    <span className="w-8 text-center text-sm font-semibold text-text-primary tabular-nums">
                                      {seasonPassQuantities[tt.id] || 0}
                                    </span>
                                    <button
                                      onClick={() => handleSeasonPassQuantityChange(tt.id, 1)}
                                      disabled={(seasonPassQuantities[tt.id] || 0) >= Math.min(pickerMax(tt), tt.availableQuantity)}
                                      className="w-9 h-9 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {Object.values(seasonPassQuantities).some(q => q > 0) && (
                              <button
                                onClick={handleSeasonPassCheckout}
                                disabled={checkoutLoading}
                                className="w-full btn-primary py-3 text-sm font-semibold"
                              >
                                {checkoutLoading ? 'Processing...' : 'Get Season Pass'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {regularTickets.length > 0 && (
                        <>
                          <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                            <div className="p-5 border-b border-surface-border">
                              <h3 className="text-lg font-semibold text-text-primary">Select Date</h3>
                            </div>
                            <div className="p-4">
                              <DatePillStrip
                                dates={availableDates}
                                selectedDate={selectedDate}
                                onSelect={handleDateSelect}
                              />
                            </div>
                          </div>

                          {selectedDate && (loadingSlots || availableTimeSlots.length !== 1) && (
                            <div ref={timeSlotSectionRef} className="scroll-mt-20 bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                              <div className="p-5 border-b border-surface-border">
                                <h3 className="text-lg font-semibold text-text-primary">Select Time</h3>
                              </div>
                              <div className="p-4">
                                {loadingSlots ? (
                                  <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-[rgb(var(--color-primary))] border-t-transparent"></div>
                                  </div>
                                ) : availableTimeSlots.length > 1 ? (
                                  <TimeSlotGrid
                                    times={availableTimeSlots}
                                    selectedTime={selectedTimeSlot}
                                    onSelect={handleTimeSlotSelect}
                                  />
                                ) : (
                                  <p className="text-center py-6 text-text-tertiary text-sm">No time slots available for this date.</p>
                                )}
                              </div>
                            </div>
                          )}

                          {showTicketSelection && !accessLocked && (
                            <div ref={ticketSectionRef} className="scroll-mt-20">
                              <BookingPanel {...bookingProps} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {position === 'bottom' && (
                    <div className="mt-8 max-w-2xl mx-auto space-y-4">
                      {/* Season Pass section — same as right sidebar version */}
                      {seasonPassTickets.length > 0 && (
                        <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                          <div className="p-5 border-b border-surface-border">
                            <h3 className="text-lg font-semibold text-text-primary">Season Passes</h3>
                            {rawEvent.endDate && (
                              <p className="text-xs text-text-tertiary mt-1">
                                Valid {new Date(rawEvent.startDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric' })} through {new Date(rawEvent.endDate).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            {seasonPassTickets.map(tt => (
                              <div key={tt.id} className="border border-surface-border rounded-xl p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-text-primary text-sm">{tt.name}</h4>
                                      {tt.badgeText && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: tt.backgroundColor || 'rgb(var(--color-primary))', color: tt.textColor || '#fff' }}>
                                          {tt.badgeText}
                                        </span>
                                      )}
                                    </div>
                                    {tt.description && <RichDescription html={tt.description} className="text-xs text-text-secondary" expandable={false} />}
                                    <div className="text-sm font-bold text-text-primary mt-2">{tt.allInPrice > 0 ? formatPrice(tt.allInPrice) : 'Free'}</div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button onClick={() => handleSeasonPassQuantityChange(tt.id, -1)} disabled={!seasonPassQuantities[tt.id]} className="w-9 h-9 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" /></svg>
                                    </button>
                                    <span className="w-8 text-center text-sm font-semibold text-text-primary tabular-nums">{seasonPassQuantities[tt.id] || 0}</span>
                                    <button onClick={() => handleSeasonPassQuantityChange(tt.id, 1)} disabled={(seasonPassQuantities[tt.id] || 0) >= Math.min(pickerMax(tt), tt.availableQuantity)} className="w-9 h-9 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {Object.values(seasonPassQuantities).some(q => q > 0) && (
                              <button onClick={handleSeasonPassCheckout} disabled={checkoutLoading} className="w-full btn-primary py-3 text-sm font-semibold">
                                {checkoutLoading ? 'Processing...' : 'Get Season Pass'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {regularTickets.length > 0 && (
                        <>
                          <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                            <div className="p-5 border-b border-surface-border">
                              <h3 className="text-lg font-semibold text-text-primary">Select Date</h3>
                            </div>
                            <div className="p-4">
                              <DatePillStrip
                                dates={availableDates}
                                selectedDate={selectedDate}
                                onSelect={handleDateSelect}
                              />
                            </div>
                          </div>

                          {selectedDate && (loadingSlots || availableTimeSlots.length !== 1) && (
                            <div ref={timeSlotSectionRef} className="scroll-mt-20 bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
                              <div className="p-5 border-b border-surface-border">
                                <h3 className="text-lg font-semibold text-text-primary">Select Time</h3>
                              </div>
                              <div className="p-4">
                                {loadingSlots ? (
                                  <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-[rgb(var(--color-primary))] border-t-transparent"></div>
                                  </div>
                                ) : availableTimeSlots.length > 1 ? (
                                  <TimeSlotGrid
                                    times={availableTimeSlots}
                                    selectedTime={selectedTimeSlot}
                                    onSelect={handleTimeSlotSelect}
                                  />
                                ) : (
                                  <p className="text-center py-6 text-text-tertiary text-sm">No time slots available for this date.</p>
                                )}
                              </div>
                            </div>
                          )}

                          {showTicketSelection && !accessLocked && (
                            <div ref={ticketSectionRef} className="scroll-mt-20">
                              <BookingPanel {...bookingProps} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {position === 'overlay' && showTicketSelection && !accessLocked && (
                    <div className="fixed bottom-8 right-8 w-96 bg-surface shadow-2xl rounded-xl border border-surface-border z-50">
                      <BookingPanel {...bookingProps} compact />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Regular event - right sidebar position */}
                  {position === 'right' && (
                    <div className="lg:col-span-1">
                      {accessLocked ? <LockedBookingCard slug={rawEvent.slug} /> : <BookingPanel {...bookingProps} />}
                    </div>
                  )}

                  {/* Regular event - bottom position */}
                  {position === 'bottom' && (
                    <div className="mt-8 max-w-2xl mx-auto">
                      {accessLocked ? <LockedBookingCard slug={rawEvent.slug} /> : <BookingPanel {...bookingProps} />}
                    </div>
                  )}

                  {/* Regular event - floating overlay position */}
                  {position === 'overlay' && !accessLocked && (
                    <div className="fixed bottom-8 right-8 w-96 bg-surface shadow-2xl rounded-xl border border-surface-border z-50">
                      <BookingPanel {...bookingProps} compact />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {eventStatus !== 'CANCELLED' && eventStatus !== 'POSTPONED' && eventStatus !== 'SOLD_OUT' && !saleWindowClosed && !isSeatedEvent && !accessLocked && (
        <div className="lg:hidden">
          {isTimedEvent && regularTickets.length === 0 && seasonPassTickets.length > 0 ? (
            /* Season-pass-only timed event: show a simple CTA bar on mobile */
            <div className="fixed bottom-0 left-0 right-0 z-50">
              <div className="bg-surface border-t border-surface-border shadow-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Season Pass</p>
                    {seasonPassTickets[0] && (
                      <p className="text-xs text-text-secondary">
                        From {seasonPassTickets[0].allInPrice > 0 ? formatPrice(seasonPassTickets[0].allInPrice) : 'Free'}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const el = document.getElementById('season-pass-section')
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }}
                    className="btn-primary px-6 py-2.5 text-sm font-semibold flex-shrink-0"
                  >
                    Get Season Pass
                  </button>
                </div>
              </div>
            </div>
          ) : isTimedEvent ? (
            <div className="fixed bottom-0 left-0 right-0 z-50">
              <div className="bg-surface border-t border-surface-border shadow-lg">
                {!selectedDate ? (
                  <div className="p-4">
                    <DatePillStrip
                      dates={availableDates}
                      selectedDate={selectedDate}
                      onSelect={handleDateSelect}
                    />
                  </div>
                ) : !selectedTimeSlot ? (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">
                          {selectedDate.start
                            ? new Date(selectedDate.start * 1000).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' })
                            : selectedDate.startDate || 'Selected'}
                        </span>
                        <span className="text-text-tertiary">|</span>
                        <h3 className="text-sm font-semibold text-text-primary">Select Time</h3>
                      </div>
                      <button
                        onClick={() => { setSelectedDate(null); setAvailableTimeSlots([]); setSelectedTickets({}) }}
                        className="text-xs text-primary font-medium"
                      >
                        Change Date
                      </button>
                    </div>
                    {loadingSlots ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
                      </div>
                    ) : (
                      <TimeSlotGrid
                        times={availableTimeSlots}
                        selectedTime={selectedTimeSlot}
                        onSelect={handleTimeSlotSelect}
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-surface-border bg-surface-secondary">
                      <span className="text-sm text-text-secondary">
                        {selectedDate.start
                          ? new Date(selectedDate.start * 1000).toLocaleDateString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' })
                          : selectedDate.startDate || 'Selected'}
                        {availableTimeSlots.length > 1 && <>
                        {' at '}
                        {(() => {
                          const raw = selectedTimeSlot.waveTime ?? selectedTimeSlot.startTimestamp ?? selectedTimeSlot.time ?? selectedTimeSlot.startTime ?? selectedTimeSlot.start
                          if (typeof raw === 'number' && raw > 86400) {
                            return new Date(raw * 1000).toLocaleTimeString('en-US', { timeZone: rawEvent.timezone || 'America/New_York', hour: 'numeric', minute: '2-digit' })
                          }
                          if (typeof raw === 'string' && /^\d{1,2}:\d{2}$/.test(raw)) {
                            const [h, m] = raw.split(':').map(Number)
                            const period = h >= 12 ? 'PM' : 'AM'
                            return `${h > 12 ? h - 12 : h === 0 ? 12 : h}:${String(m).padStart(2, '0')} ${period}`
                          }
                          return String(raw || '')
                        })()}
                        </>}
                      </span>
                      <button
                        onClick={() => {
                          if (availableTimeSlots.length <= 1) {
                            setSelectedDate(null); setSelectedTimeSlot(null); setAvailableTimeSlots([]); setSelectedTickets({})
                          } else {
                            setSelectedTimeSlot(null); setSelectedTickets({})
                          }
                        }}
                        className="text-xs text-primary font-medium"
                      >
                        {availableTimeSlots.length <= 1 ? 'Change Date' : 'Change'}
                      </button>
                    </div>
                    <MobileBookingSheet {...bookingProps} autoExpand={!rawEvent.showDescriptionFirst} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <MobileBookingSheet {...bookingProps} expandRef={mobileSheetExpandRef} autoExpand={!rawEvent.showDescriptionFirst} />
          )}
        </div>
      )}

      {/* Mobile countdown when sale window is closed */}
      {saleWindowClosed && eventStatus !== 'CANCELLED' && eventStatus !== 'POSTPONED' && eventStatus !== 'SOLD_OUT' && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
          <div className="bg-surface border-t border-surface-border shadow-lg p-4">
            <SaleCountdownCard event={rawEvent} eventStatus={eventStatus} compact />
            {salePhase?.type === 'presale_active' && !presaleUnlocked && (
              <div className="mt-3">
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={presaleCode}
                    onChange={(e) => { setPresaleCode(e.target.value.toUpperCase()); setPresaleError('') }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handlePresaleVerify() }}
                    placeholder="Pre-sale code..."
                    className="flex-1 min-w-0 px-3 py-2 bg-surface-secondary border border-surface-border rounded-btn text-text-primary placeholder:text-text-tertiary focus:ring-2 focus:ring-primary focus:border-primary uppercase"
                    style={{ fontSize: '16px' }}
                    disabled={presaleVerifying}
                  />
                  <button
                    onClick={handlePresaleVerify}
                    disabled={presaleVerifying || !presaleCode.trim()}
                    className="shrink-0 px-4 py-2 bg-primary text-text-inverse rounded-btn text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
                  >
                    {presaleVerifying ? '...' : 'Unlock'}
                  </button>
                </div>
                {presaleError && (
                  <p className="text-xs text-error mt-1.5">{presaleError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
